import { fiveYearsFromNow } from 'common'
import { getValue, removeSpacesAndDashes } from 'utils'
import copy from 'copy.json'
import {
  exactLength,
  futureDate,
  includesNoNumbers,
  isNumeric,
  maxDate,
  maxLength,
  maxTwoDecimals,
  maxValue,
  minDate,
  minValue,
  minValueExcluding,
  noDecimals,
  noPolishLetters,
  required,
  validAlphanumericField,
  validContractLength,
  validDate,
  validEmail,
  validEnergyTariffGroup,
  validPolishAlphanumericField,
  validTaxIdentifier,
  validZipCode,
  validAzimuth
} from 'utils/validators'

export const validateRegistrationForm = ({ name, value }: HTMLInputElement) => {
  switch (name) {
    case 'tax_identifier':
      return required(value) || validTaxIdentifier(value)
    case 'company_name':
      return (
        required(value) ||
        validPolishAlphanumericField(value, copy.validators.company_name) ||
        maxLength(value, 100)
      )
    case 'concession_identifier':
      return (
        required(value) ||
        validAlphanumericField(value, copy.validators.concession_identifier) ||
        maxLength(value, 100)
      )
    case 'phone_number':
      const _value = removeSpacesAndDashes(value)

      return required(_value) || exactLength(_value, 9) || isNumeric(_value) || noDecimals(_value)
    case 'contact_email':
      return required(value) || noPolishLetters(value) || validEmail(value) || maxLength(value, 100)
    case 'registration_source':
      return required(value)
    default:
      return undefined
  }
}

export const validateOfferForm = ({ name, value }: HTMLInputElement) => {
  const minimalContractLength = getValue('minimal_contract_length')
  const maximalContractLength = getValue('maximal_contract_length')

  switch (name) {
    case 'installation_power':
      return (
        required(value) ||
        minValueExcluding(value, 0) ||
        maxValue(value, 1000000) ||
        maxTwoDecimals(value)
      )
    case 'location_city':
      return (
        required(value) ||
        validPolishAlphanumericField(value, copy.validators.location_city) ||
        includesNoNumbers(value) ||
        maxLength(value, 100)
      )
    case 'location_zip_code':
      return required(value) || validZipCode(value)
    case 'osd_name':
      return (
        required(value) ||
        validPolishAlphanumericField(value, copy.validators.osd_name) ||
        maxLength(value, 100)
      )
    case 'energy_point_number':
      return required(value) || validAlphanumericField(value) || maxLength(value, 100)
    case 'building_permit_number':
      return required(value) || validAlphanumericField(value) || maxLength(value, 100)
    case 'production_start_date':
      return (
        required(value) ||
        validDate(value) ||
        minDate(value, '1970-01-01') ||
        maxDate(value, fiveYearsFromNow)
      )
    case 'yearly_power_production':
      return required(value) || minValue(value, 1) || maxValue(value, 1000000) || noDecimals(value)
    case 'minimal_contract_length':
      if (value && maximalContractLength && parseInt(value) > parseInt(maximalContractLength)) {
        return copy.validators.minimal_contract_length
      }

      return (
        required(value) || validContractLength(value) || maxValue(value, 240) || noDecimals(value)
      )
    case 'maximal_contract_length':
      if (value && minimalContractLength && parseInt(value) < parseInt(minimalContractLength)) {
        return copy.validators.maximal_contract_length
      }

      return (
        required(value) || validContractLength(value) || maxValue(value, 240) || noDecimals(value)
      )
    case 'electric_energy_meter_number':
      return required(value) || validAlphanumericField(value) || maxLength(value, 100)
    case 'energy_price':
      return (
        required(value) || minValue(value, 1) || maxValue(value, 10000) || maxTwoDecimals(value)
      )
    case 'energy_multiplier':
      return required(value) || minValue(value, 1) || maxValue(value, 1000000) || noDecimals(value)
    case 'profileFile':
      return required(value)
    default:
      return undefined
  }
}

export const validateDemandForm = ({ name, value }: HTMLInputElement, isEdited?: boolean) => {
  const currentContractEndDate = getValue('current_contract_end_date')

  switch (name) {
    case 'location_city':
      return (
        required(value) ||
        validPolishAlphanumericField(value, copy.validators.location_city) ||
        includesNoNumbers(value) ||
        maxLength(value, 100)
      )
    case 'location_zip_code':
      return required(value) || validZipCode(value)
    case 'osd_name':
      return (
        required(value) ||
        validPolishAlphanumericField(value, copy.validators.osd_name) ||
        maxLength(value, 100)
      )
    case 'yearly_power_consumption':
      return required(value) || minValue(value, 1) || maxValue(value, 1000000) || noDecimals(value)
    case 'contract_start_date':
      if (currentContractEndDate && value && new Date(currentContractEndDate) > new Date(value)) {
        return copy.validators.contract_start_date
      }

      return (
        required(value) ||
        validDate(value) ||
        maxDate(value, fiveYearsFromNow) ||
        (isEdited ? undefined : futureDate(value))
      )
    case 'electric_energy_meter_number':
      return required(value) || validAlphanumericField(value) || maxLength(value, 100)
    case 'energy_point_number':
      return required(value) || validAlphanumericField(value) || maxLength(value, 100)
    case 'energy_tariff_group':
      const _value = removeSpacesAndDashes(value)

      return required(_value) || validEnergyTariffGroup(_value) || maxLength(_value, 6)
    case 'current_energy_price':
      return (
        required(value) || minValue(value, 1) || maxValue(value, 10000) || maxTwoDecimals(value)
      )
    case 'current_energy_provider_name':
      return value
        ? validPolishAlphanumericField(value, copy.validators.current_energy_provider_name) ||
            maxLength(value, 100)
        : undefined
    case 'current_contract_end_date':
      return value
        ? validDate(value) ||
            maxDate(value, fiveYearsFromNow) ||
            (isEdited ? undefined : futureDate(value))
        : undefined
    case 'contract_length':
      return required(value) || minValue(value, 1) || maxValue(value, 240) || noDecimals(value)
    case 'energy_multiplier':
      return required(value) || minValue(value, 1) || maxValue(value, 1000000) || noDecimals(value)
    case 'profileFile':
      return required(value)
    default:
      return undefined
  }
}

export const validateStatisticDataForm = ({ name, value }: HTMLInputElement) => {
  switch (name) {
    case 'azimuth':
      return required(value) || validAzimuth(Number(value))
    case 'slope':
    case 'system_loss':
      return required(value)
    default:
      return undefined
  }
}
